import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { DEFAULT_LANGUAGE, intlLocale, type KioskLanguage } from "./languages";
import {
  activateLanguage,
  deactivateLanguage,
  getVersion,
  loadLanguage,
  rememberTabletLanguage,
  storedTabletLanguage,
  subscribe,
} from "./store";
import { t, type TranslateFn } from "./t";

/**
 * G-85 — the kiosk's language, in the shape of `ThemeProvider`.
 *
 * THREE layers, and the order is the whole design (owner ruling 2026-09-20):
 *
 *   1. `override`  — someone picked a language from the lock-screen dropdown.
 *                    It WINS over the person's own preference, because the
 *                    person standing at the tablet asked for it out loud.
 *                    Not persisted, and cleared on lock: it is a one-shift
 *                    choice, so the next florist never inherits it.
 *   2. `person`    — the language on the signed-in florist's own record,
 *                    delivered by the PIN response (G-85 S6). Null when they
 *                    have never chosen one.
 *   3. `tablet`    — what this tablet shows to whoever walks up, persisted in
 *                    `localStorage["fw-lang"]`, falling back to
 *                    `KIOSK_DEFAULT_LANGUAGE` (Spanish) on a tablet nobody
 *                    has chosen on yet. The dropdown writes it, which is what
 *                    makes a Russian manager's shift leave the tablet
 *                    speaking Russian to the Russians who follow them — while
 *                    anyone whose own language is set still overrides it at
 *                    the PIN.
 *
 * The rule the three layers encode (owner, 2026-09-20): A LANGUAGE PICKER
 * EXISTS ONLY WHERE THE APP DOES NOT KNOW WHO YOU ARE. Everywhere else the
 * language follows the person — which is why the hub has no switcher, why the
 * CRM will have none either, and why the lock screen keeps this one.
 *
 * The owner's words: "language can be picked by the user on locked screen …
 * when user enters their pin, their user is carrying preferred language so the
 * kiosk mode opens in that language" and "if they don't select language there,
 * it will load their default language".
 *
 * Mounted around the kiosk route ONLY. On unmount the store goes back to
 * English and `<html lang>` follows, so the CRM never inherits a florist's
 * choice — the `lockTheme`/`unlockTheme` shape.
 */

interface LanguageContextValue {
  /** The effective language on screen. */
  language: KioskLanguage;
  /**
   * True while a lock-screen pick is overriding the signed-in person's own
   * language. When false the dropdown shows its resting option, which NAMES
   * the language on screen so the label can never contradict it.
   */
  hasOverride: boolean;
  /**
   * The lock-screen dropdown. A language sets BOTH the tablet default (so the
   * lock screen keeps it) and the override (so it beats the person's own
   * preference for this shift). `null` clears the override and hands control
   * back to whoever signs in.
   */
  chooseLanguage: (next: KioskLanguage | null) => void;
  /** The signed-in person's own language, from the PIN response. */
  setPersonLanguage: (next: KioskLanguage | null) => void;
  /** Screen locked or signed out: forget the person AND the override. */
  clearSession: () => void;
}

const LanguageContext = createContext<LanguageContextValue>({
  // English outside the provider — that is the CRM's language.
  language: DEFAULT_LANGUAGE,
  hasOverride: false,
  chooseLanguage: () => undefined,
  setPersonLanguage: () => undefined,
  clearSession: () => undefined,
});

function stampDocumentLanguage(language: KioskLanguage) {
  document.documentElement.setAttribute("lang", intlLocale(language));
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [tabletDefault, setTabletDefaultState] = useState<KioskLanguage>(() =>
    storedTabletLanguage(),
  );
  const [override, setOverride] = useState<KioskLanguage | null>(null);
  const [person, setPerson] = useState<KioskLanguage | null>(null);

  const language = override ?? person ?? tabletDefault;

  const chooseLanguage = useCallback((next: KioskLanguage | null) => {
    setOverride(next);
    if (next) {
      // The tablet REMEMBERS it. That is the point of the memory: a Russian
      // manager who starts the shift leaves the counter speaking Russian to
      // the Russians who follow them, without anyone having to pick again.
      // It is only a fallback — a florist whose own language is set still
      // overrides it at the PIN.
      rememberTabletLanguage(next);
      setTabletDefaultState(next);
    }
  }, []);

  const clearSession = useCallback(() => {
    setOverride(null);
    setPerson(null);
  }, []);

  useEffect(() => {
    // Activate first, load second: the language and `<html lang>` are right
    // immediately, English shows until the chunk lands, then the store's
    // version bump repaints every subscriber. Never a blank screen.
    activateLanguage(language);
    stampDocumentLanguage(language);
    let alive = true;
    void loadLanguage(language).catch((error: unknown) => {
      if (!alive) return;
      // A failed chunk (offline, or a stale index after a deploy) is not
      // worth breaking the counter for — English stays, and G-72's console
      // buffer carries the warning into any bug report filed meanwhile.
      console.warn(`Could not load the ${language} dictionary`, error);
    });
    return () => {
      alive = false;
    };
  }, [language]);

  useEffect(
    () => () => {
      deactivateLanguage();
      stampDocumentLanguage(DEFAULT_LANGUAGE);
    },
    [],
  );

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      hasOverride: override !== null,
      chooseLanguage,
      setPersonLanguage: setPerson,
      clearSession,
    }),
    [language, override, chooseLanguage, clearSession],
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  return useContext(LanguageContext);
}

/**
 * `t` for components. Subscribes to the store, so a component repaints when
 * the language switches or its dictionary finishes loading. Works OUTSIDE the
 * provider too (shared components rendered in the CRM): the store is then
 * simply "en".
 */
export function useT(): { t: TranslateFn; language: KioskLanguage } {
  const version = useSyncExternalStore(subscribe, getVersion, getVersion);
  const { language } = useLanguage();
  // `version` is the dependency on purpose: the function is the same, but a
  // new object tells memoised callers that its answers changed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => ({ t, language }), [version, language]);
}
